import apiClient, { uploadFilesByType } from 'api/base';
import {
  BroadcastDeliveryStatuses,
  GetAllItemsParams,
  isLocalFile,
  isServerAttachment,
} from 'interfaces';
import {
  Broadcast,
  BroadcastDeliveriesProgress,
  BroadcastDelivery,
  BroadcastDeliveryStatusesTypes,
  DraftBroadcast,
} from 'models/broadcast.model';

interface BroadcastInfo {
  title: string;
  message: string;
  tagIds: string[];
  userRoles: string[];
  schedule: string;
}

export const getBroadcasts = async ({
  status,
  isOwner,
  favourites,
}: GetAllItemsParams): Promise<Broadcast[]> => {
  const queries = [];

  if (status) {
    queries.push(`status=${status}`);
  }
  if (isOwner) {
    queries.push(`isOwner=${isOwner}`);
  }
  if (favourites) {
    queries.push('favourites=true');
  }

  const query = queries.join('&');

  const { data } = await apiClient.get<Broadcast[]>(
    `/broadcasts${query ? `?${query}` : ''}`,
  );

  return data;
};

export const getBroadcast = async (id: string): Promise<Broadcast> => {
  const { data } = await apiClient.get<Broadcast>(`/broadcasts/${id}`);

  return data;
};

export const draftBroadcast = async (
  broadcast: BroadcastInfo,
): Promise<Broadcast> => {
  const { data } = await apiClient.post<Broadcast>('/broadcasts', {
    ...broadcast,
  });

  return data;
};

export const getBroadcastDeliveriesProgress = async (
  id: string,
): Promise<BroadcastDeliveriesProgress> => {
  const { data } = await apiClient.get(`/broadcasts/${id}/delivery-status`);

  return data;
};

export const getBroadcastDeliveriesByStatus = async (
  id: string,
  status: BroadcastDeliveryStatusesTypes,
): Promise<BroadcastDelivery> => {
  const { data } = await apiClient.get(`/broadcasts/${id}/deliveries`, {
    params: {
      status,
      limit: 100,
      cursor: '0',
    },
  });

  return data;
};

export const getBroadcastDeliveriesByAllStatuses = async (
  id: string,
): Promise<Record<BroadcastDeliveryStatusesTypes, BroadcastDelivery>> => {
  const statuses = Object.keys(
    BroadcastDeliveryStatuses,
  ) as BroadcastDeliveryStatusesTypes[];

  const responses = await Promise.allSettled(
    statuses.map(async (s) => {
      const data = await getBroadcastDeliveriesByStatus(id, s);

      return {
        data,
        status: s,
      };
    }),
  );

  return responses.reduce(
    (acc, response) => {
      if (response.status === 'fulfilled') {
        acc[response.value.status] = response.value.data;
      } else {
        console.error(
          'Failed to fetch deliveries for a status:',
          response.reason,
        );
      }

      return acc;
    },
    {} as Record<BroadcastDeliveryStatusesTypes, BroadcastDelivery>,
  );
};

export const updateBroadcast = async (
  id: string,
  broadcast: Omit<DraftBroadcast, 'status' | 'scheduleTime'>,
): Promise<Broadcast> => {
  const { data } = await apiClient.put<Broadcast>(
    `/broadcasts/${id}`,
    broadcast,
  );

  return data;
};

export const reviewBroadcast = async (id: string): Promise<Broadcast> => {
  const { data } = await apiClient.patch<Broadcast>(`/broadcasts/${id}/review`);

  return data;
};

export const uploadFiles = async (
  id: string,
  attachments: File[],
): Promise<Broadcast> => {
  await uploadFilesByType(id, attachments, 'broadcasts');

  const data = await getBroadcast(id);

  return data;
};

export const createBroadcastAndReview = async (
  broadcast: BroadcastInfo,
  attachments: File[],
): Promise<Broadcast> => {
  try {
    const updatedBroadcast = await draftBroadcast(broadcast);

    if (attachments.length) {
      await uploadFiles(updatedBroadcast.id, attachments);
    }

    const data = await reviewBroadcast(updatedBroadcast.id);

    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const updateBroadcastAndReview = async (
  broadcast: Omit<
    Broadcast,
    | 'status'
    | 'author'
    | 'authorId'
    | 'createdAt'
    | 'updatedAt'
    | 'scheduleTime'
  >,
): Promise<Broadcast> => {
  try {
    const updatedBroadcast = await updateBroadcast(broadcast.id, {
      ...broadcast,
      attachments: broadcast.attachments.filter(isServerAttachment),
    });

    const notSavedAttachments = broadcast.attachments.filter(isLocalFile);

    if (notSavedAttachments.length) {
      await uploadFiles(updatedBroadcast.id, notSavedAttachments);
    }

    const data = await reviewBroadcast(updatedBroadcast.id);

    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const rejectBroadcast = async (id: string): Promise<Broadcast> => {
  const { data } = await apiClient.patch<Broadcast>(`/broadcasts/${id}/reject`);

  return data;
};

export const approveBroadcast = async (id: string): Promise<Broadcast> => {
  const { data } = await apiClient.patch<Broadcast>(
    `/broadcasts/${id}/approve`,
  );

  return data;
};

export const reopenBroadcast = async (id: string): Promise<Broadcast> => {
  const { data } = await apiClient.patch<Broadcast>(`/broadcasts/${id}/reopen`);

  return data;
};

export const deleteBroadcast = async (id: string): Promise<Broadcast> => {
  const { data } = await apiClient.delete<Broadcast>(`/broadcasts/${id}`);

  return data;
};
